import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieGrid from '@/components/feature/MovieGrid';
import { getMoviesByIds, getTopicById, topics } from '@/lib/movieData';
import useDocumentMeta from '@/hooks/useDocumentMeta';

export default function TopicPage() {
  const { id = '' } = useParams();
  const topic = getTopicById(id);

  useDocumentMeta(
    topic ? `${topic.title} - 久久热精选专题` : '专题不存在 - 久久热',
    topic ? `${topic.subtitle}。${topic.intro}` : '未找到该专题，返回久久热首页浏览更多影视内容。'
  );

  if (!topic) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="inline-flex w-16 h-16 rounded-full bg-background-100 items-center justify-center">
          <i className="ri-bookmark-3-line text-3xl text-foreground-400" />
        </span>
        <h1 className="mt-5 font-display text-3xl font-bold text-foreground-950">专题不存在</h1>
        <p className="mt-3 text-foreground-500">该专题可能已被下架，看看下面其他精彩专题吧。</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-4-line text-lg" />返回首页
          </Link>
        </div>
      </div>
    );
  }

  const others = topics.filter((t) => t.id !== topic.id);
  const totalCount = topic.groups.reduce((acc, g) => acc + g.movieIds.length, 0);

  return (
    <div className="w-full">
      {/* Cover */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img src={topic.cover} alt={`${topic.title} 专题主图`} title={`${topic.title} - 久久热精选专题`} className="w-full h-full object-cover object-top opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/40" />
        </div>
        <div className="relative w-full px-4 md:px-8 py-10 md:py-16">
          <div className="text-background-200/80">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '精选专题' }, { label: topic.title }]} />
          </div>
          <span className="mt-6 inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-accent-500 text-accent-950 text-xs font-semibold">
            <i className="ri-bookmark-3-fill text-xs" />
            编辑精选专题 · {totalCount} 部作品
          </span>
          <h1 className="mt-3 font-display text-3xl md:text-5xl font-extrabold text-background-50 max-w-3xl">{topic.title}</h1>
          <p className="mt-3 text-base md:text-lg text-accent-400 font-medium">{topic.subtitle}</p>
          <p className="mt-5 max-w-2xl text-sm md:text-base text-background-100/85 leading-relaxed">{topic.intro}</p>
        </div>
      </section>

      {/* Groups */}
      <div className="w-full px-4 md:px-8 py-10 md:py-14 space-y-12">
        {topic.groups.map((group, gi) => {
          const list = getMoviesByIds(group.movieIds);
          return (
            <section key={group.name} id={`group-${gi + 1}`}>
              <div className="flex flex-wrap items-start gap-3 md:gap-5 mb-5">
                <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 font-display text-xl font-bold flex items-center justify-center shrink-0">
                  {String(gi + 1).padStart(2, '0')}
                </span>
                <div>
                  <h2 className="font-display text-xl md:text-2xl font-bold text-foreground-950">
                    <a href={`#group-${gi + 1}`} className="hover:text-primary-600 transition-colors cursor-pointer">{group.name}</a>
                  </h2>
                  <p className="mt-1 text-sm text-foreground-500 max-w-2xl">{group.reason}</p>
                </div>
              </div>
              <MovieGrid movies={list} columns={4} />
            </section>
          );
        })}
      </div>

      {/* Other topics */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
        <h2 className="font-display text-xl md:text-2xl font-bold text-foreground-950">更多精选专题</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {others.map((t) => (
            <Link key={t.id} to={`/topic/${t.id}`} className="group flex gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer">
              <div className="w-32 h-20 sm:w-40 sm:h-24 rounded-md overflow-hidden bg-background-200 shrink-0">
                <img src={t.cover} alt={`${t.title} 专题封面`} title={`${t.title} - 久久热精选专题`} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="min-w-0 py-1">
                <h3 className="font-display text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">{t.title}</h3>
                <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2 leading-relaxed">{t.subtitle}</p>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-600">
                  查看专题 <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}